import { endOfMonth, format, isValid, parseISO, startOfMonth } from 'date-fns';
import { apiDateTimeToLocalInput, localInputToApiDateTime } from './formatters';

export const JOB_STATUSES = [
    { value: 'em_andamento', label: 'Em andamento' },
    { value: 'sucesso', label: 'Sucesso' },
    { value: 'falha', label: 'Falha' },
    { value: 'cancelado', label: 'Cancelado' },
    { value: 'parcial', label: 'Parcial' },
    { value: 'fator_externo', label: 'Fator externo' },
];

/** Special list-only filter (not a real job status). */
export const JOB_FILAMENT_PENDING_FILTER = 'filament_pending';

export const JOB_LIST_STATUS_FILTERS = [
    ...JOB_STATUSES,
    { value: JOB_FILAMENT_PENDING_FILTER, label: 'Consumo não informado' },
];

export const STATUSES_REQUIRING_REASON = ['falha', 'cancelado', 'parcial', 'fator_externo'];

export const STATUSES_SYNC_PRINTED_COUNT = ['fator_externo'];

/** Duração padrão no cadastro de job (1 hora). */
export const DEFAULT_ESTIMATED_DURATION = '01:00';

/** inputProps para teclado numérico em mobile (MUI TextField). */
export function numericInputProps(min) {
    const props = { inputMode: 'numeric' };
    if (min !== undefined) {
        props.min = min;
    }
    return props;
}

export function statusLabel(value) {
    const found = JOB_STATUSES.find((s) => s.value === value);
    return found ? found.label : value || '—';
}

const JOB_STATUS_BADGE = {
    em_andamento: { label: 'Em andamento', color: 'info' },
    sucesso: { label: 'Sucesso', color: 'success' },
    parcial: { label: 'Parcial', color: 'warning' },
    falha: { label: 'Falha', color: 'error' },
    fator_externo: { label: 'Fator externo', color: 'secondary' },
    cancelado: {
        label: 'Cancelado',
        sx: {
            bgcolor: '#ed6c02',
            color: '#fff',
            fontWeight: 600,
        },
    },
};

/** Props para MUI Chip na listagem de jobs. */
export function getJobStatusBadgeProps(status) {
    const config = JOB_STATUS_BADGE[status];
    if (!config) {
        return { label: statusLabel(status), size: 'small', color: 'default' };
    }
    return { label: config.label, size: 'small', color: config.color, sx: config.sx };
}

/** Status em que a impressão não seguiu o previsto, exigindo o consumo real. */
export const STATUSES_REQUIRING_ACTUAL_CONSUMPTION = ['falha', 'cancelado', 'parcial', 'fator_externo'];

/**
 * Uma impressão em andamento ainda não consumiu nada: o estoque só é debitado
 * quando ela chega a um desfecho, e é aí que o vínculo passa a ser obrigatório.
 */
export function statusCountsFilamentUsage(status) {
    return String(status || '').trim() !== 'em_andamento';
}

export function requiresActualConsumption(status) {
    return STATUSES_REQUIRING_ACTUAL_CONSUMPTION.includes(status);
}

export function emptyJobFilamentRow() {
    return { filament_uuid: '', grams: '', label: '' };
}

/** Converte os filamentos vindos da API nas linhas do formulário. */
export function jobFilamentsToRows(filaments) {
    if (!Array.isArray(filaments) || filaments.length === 0) {
        return [emptyJobFilamentRow()];
    }
    const rows = filaments
        .filter((f) => f && String(f.filament_uuid || '').trim())
        .map((f) => ({
            filament_uuid: String(f.filament_uuid).trim(),
            grams: String(f.grams ?? ''),
            label: [f.type, f.brand, f.color_label]
                .map((part) => String(part || '').trim())
                .filter(Boolean)
                .join(' · '),
        }));
    return rows.length ? rows : [emptyJobFilamentRow()];
}

/**
 * Total que a distribuição precisa somar: o consumo real quando informado, o
 * estimado caso contrário.
 */
export function effectiveConsumptionGrams(form) {
    if (requiresActualConsumption(form?.status)) {
        const actual = parseInt(form?.actual_filament_consumption_grams, 10);
        if (Number.isFinite(actual)) {
            return actual;
        }
    }
    return parseInt(form?.estimated_filament_consumption_grams, 10) || 0;
}

export function sumFilamentRows(rows) {
    if (!Array.isArray(rows)) {
        return 0;
    }
    return rows
        .filter((row) => String(row?.filament_uuid || '').trim())
        .reduce((total, row) => total + (parseInt(row.grams, 10) || 0), 0);
}

/**
 * Valida a seção de filamentos contra o total da impressão. Retorna a mensagem
 * de erro ou string vazia quando está tudo certo.
 */
export function validateFilamentDistribution(form, rows) {
    const linked = (rows || []).filter((row) => String(row?.filament_uuid || '').trim());

    if (linked.length === 0) {
        if (!statusCountsFilamentUsage(form?.status)) {
            return '';
        }
        return 'Vincule ao menos um filamento cadastrado a esta impressão.';
    }

    const seen = new Set();
    for (const row of linked) {
        if (seen.has(row.filament_uuid)) {
            return 'O mesmo filamento foi informado mais de uma vez. Use uma linha por rolo.';
        }
        seen.add(row.filament_uuid);
        if ((parseInt(row.grams, 10) || 0) < 0) {
            return 'A quantidade utilizada não pode ser negativa.';
        }
    }

    const total = sumFilamentRows(rows);
    const expected = effectiveConsumptionGrams(form);
    if (total === expected) {
        return '';
    }

    const usingActual = requiresActualConsumption(form?.status);
    const reference = usingActual ? 'o consumo real' : 'o consumo estimado';
    const difference = Math.abs(total - expected);
    const direction = total > expected ? 'a mais' : 'a menos';

    return `A distribuição entre os filamentos não corresponde ao total informado para a impressão. `
        + `A soma das quantidades utilizadas é de ${total}g, ${difference}g ${direction} do que `
        + `${reference} de ${expected}g. Ajuste as quantidades ou o total para que os dois valores fiquem iguais.`;
}

export function toInputDateTime(dbValue) {
    return apiDateTimeToLocalInput(dbValue) || '';
}

/** Converte minutos (API) para HH:MM (formulário). */
export function minutesToDuration(totalMinutes) {
    const total = Math.max(0, parseInt(totalMinutes, 10) || 0);
    const hours = Math.floor(total / 60);
    const minutes = total % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Converte HH:MM para minutos (API), mesma lógica do TimeInputWithBlur / calculadora.
 * Retorna null se os minutos forem inválidos (> 59).
 */
export function durationToMinutes(value) {
    if (value == null || String(value).trim() === '') {
        return 0;
    }
    const cleaned = String(value).replace(/\D/g, '');
    if (cleaned === '') {
        return 0;
    }
    const hours = parseInt(cleaned.slice(0, 2), 10) || 0;
    const minutes = parseInt(cleaned.slice(2, 4), 10) || 0;
    if (minutes > 59) {
        return null;
    }
    return hours * 60 + minutes;
}

export function parseJobsDateInput(value) {
    if (!value || !String(value).trim()) {
        return null;
    }
    const day = String(value).trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return null;
    }
    const parsed = parseISO(day);
    return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : null;
}

/** Intervalo do mês civil atual (dia 01 → último dia), em YYYY-MM-DD. */
export function currentMonthJobsDateRange(referenceDate = new Date()) {
    const day = referenceDate instanceof Date && isValid(referenceDate) ? referenceDate : new Date();
    return {
        from: format(startOfMonth(day), 'yyyy-MM-dd'),
        to: format(endOfMonth(day), 'yyyy-MM-dd'),
    };
}

/** Ordena início/fim do filtro e preenche dia único. Sem limite de duração. */
export function normalizeJobsDateRange(startDate, endDate) {
    let start = parseJobsDateInput(startDate);
    let end = parseJobsDateInput(endDate);

    if (!start && !end) {
        return { start: '', end: '' };
    }
    if (start && !end) {
        end = start;
    }
    if (!start && end) {
        start = end;
    }
    if (start > end) {
        [start, end] = [end, start];
    }

    return { start, end };
}

/** Período para printer-stats: sem limite de 1 mês. */
export function jobsDateRangeForStatsApi(startDate, endDate) {
    const { start, end } = normalizeJobsDateRange(startDate, endDate);
    if (!start && !end) {
        return { finished_from: null, finished_to: null, error: null };
    }
    if (!start || !end) {
        return {
            finished_from: null,
            finished_to: null,
            error: 'Informe a data inicial e a data final.',
        };
    }

    return {
        finished_from: start,
        finished_to: end,
        error: null,
    };
}

export const PRINTER_STATS_PERIOD_MODES = [
    { value: 'all_time', label: 'Todo o tempo' },
    { value: 'custom', label: 'Período específico' },
];

export function buildPrinterStatsQuery({ printerUuid, status, finishedFrom, finishedTo, q } = {}) {
    const params = new URLSearchParams();
    if (q) {
        params.set('q', q);
    }
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (status) {
        params.set('status', status);
    }
    if (finishedFrom) {
        params.set('finished_from', finishedFrom);
    }
    if (finishedTo) {
        params.set('finished_to', finishedTo);
    }
    return params.toString();
}

export function resolvePrinterStatsPeriod({
    mode,
    customFrom,
    customTo,
}) {
    if (mode === 'all_time') {
        return {
            printerUuid: '',
            status: '',
            finishedFrom: '',
            finishedTo: '',
            error: null,
        };
    }

    const range = jobsDateRangeForStatsApi(customFrom, customTo);
    if (range.error) {
        return { error: range.error };
    }

    return {
        printerUuid: '',
        status: '',
        finishedFrom: range.finished_from || '',
        finishedTo: range.finished_to || '',
        error: null,
    };
}

/** Converte intervalo de datas do filtro em parâmetros da API (YYYY-MM-DD). */
export function jobsDateRangeForApi(startDate, endDate) {
    const { start, end } = normalizeJobsDateRange(startDate, endDate);
    if (!start && !end) {
        return { finished_from: null, finished_to: null, error: null };
    }
    if (!start || !end) {
        return {
            finished_from: null,
            finished_to: null,
            error: 'Informe a data inicial e a data final.',
        };
    }

    return {
        finished_from: start,
        finished_to: end,
        error: null,
    };
}

export function buildJobsFilterQuery({ q, printerUuid, status, finishedFrom, finishedTo }) {
    const params = new URLSearchParams();
    if (q) {
        params.set('q', q);
    }
    if (printerUuid) {
        params.set('printer_uuid', printerUuid);
    }
    if (status) {
        params.set('status', status);
    }
    if (finishedFrom) {
        params.set('finished_from', finishedFrom);
    }
    if (finishedTo) {
        params.set('finished_to', finishedTo);
    }
    return params.toString();
}

export function hasActiveJobsQueryFilters(queryFilters, searchQuery = '') {
    return Boolean(
        queryFilters?.printer_uuid
        || queryFilters?.status
        || queryFilters?.finished_from
        || queryFilters?.finished_to
        || String(searchQuery || '').trim(),
    );
}

/** Parâmetros compartilhados entre listagem e printer-stats. */
export function buildSharedJobsQueryParams(queryFilters, { q = '' } = {}) {
    return {
        printerUuid: queryFilters?.printer_uuid || '',
        status: queryFilters?.status || '',
        finishedFrom: queryFilters?.finished_from || '',
        finishedTo: queryFilters?.finished_to || '',
        q: String(q || '').trim(),
    };
}

export function resolvePrinterStatsQueryParams({
    mode,
    customFrom,
    customTo,
}) {
    const period = resolvePrinterStatsPeriod({ mode, customFrom, customTo });
    if (period.error) {
        return { error: period.error };
    }

    if (mode === 'all_time') {
        return {
            error: null,
            params: {
                printerUuid: '',
                status: '',
                finishedFrom: '',
                finishedTo: '',
                q: '',
            },
        };
    }

    return {
        error: null,
        params: {
            q: '',
            printerUuid: '',
            status: '',
            finishedFrom: period.finishedFrom || '',
            finishedTo: period.finishedTo || '',
        },
    };
}

export function sumPrinterStatsJobs(stats) {
    return (stats || []).reduce((acc, row) => acc + (Number(row?.total_jobs) || 0), 0);
}

export function buildJobsListQuery({ page, limit, q, printerUuid, status, finishedFrom, finishedTo }) {
    const params = new URLSearchParams(buildJobsFilterQuery({
        q,
        printerUuid,
        status,
        finishedFrom,
        finishedTo,
    }));
    params.set('page', String(page));
    params.set('limit', String(limit));
    return params.toString();
}

/** Monta cards de impressoras com taxa de sucesso (ordem de cadastro preservada). */
export function buildPrinterSuccessCards(printers, stats, inProgressSet = new Set()) {
    const statsMap = Object.fromEntries((stats || []).map((item) => [item.printer_uuid, item]));
    const printerIds = new Set((printers || []).map((p) => p.id));

    const cards = (printers || []).map((printer) => {
        const stat = statsMap[printer.id];
        return {
            printer_uuid: printer.id,
            name: printer.name,
            brand: printer.brand,
            snapshot_url: printer.snapshot_url,
            success_rate: stat?.success_rate ?? 0,
            total_jobs: stat?.total_jobs ?? 0,
            total_objects: stat?.total_objects ?? 0,
            total_printed: stat?.total_printed ?? 0,
            total_duration_minutes: stat?.total_duration_minutes ?? 0,
            total_filament_grams: stat?.total_filament_grams ?? 0,
            is_printing: inProgressSet.has(printer.id),
            filaments: stat?.filaments || printer.filaments || [],
            archived: printer.active === false,
        };
    });

    (stats || []).forEach((stat) => {
        if (!stat?.printer_uuid || printerIds.has(stat.printer_uuid)) {
            return;
        }
        cards.push({
            printer_uuid: stat.printer_uuid,
            name: stat.printer_uuid,
            brand: '',
            success_rate: stat.success_rate ?? 0,
            total_jobs: stat.total_jobs ?? 0,
            total_objects: stat.total_objects ?? 0,
            total_printed: stat.total_printed ?? 0,
            total_duration_minutes: stat.total_duration_minutes ?? 0,
            total_filament_grams: stat.total_filament_grams ?? 0,
            is_printing: inProgressSet.has(stat.printer_uuid),
            filaments: stat.filaments || [],
            archived: true,
        });
    });

    return cards;
}

/** Duração de produção legível (ex.: 42h 30min). */
export function formatProductionDurationMinutes(totalMinutes) {
    const minutes = Math.max(0, Math.round(Number(totalMinutes)) || 0);
    if (minutes === 0) {
        return '0min';
    }
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (hours === 0) {
        return `${remainder}min`;
    }
    if (remainder === 0) {
        return `${hours}h`;
    }
    return `${hours}h ${remainder}min`;
}

/** Filamento estimado em gramas ou quilos. */
export function formatFilamentGrams(grams) {
    const value = Math.max(0, Math.round(Number(grams)) || 0);
    if (value >= 1000) {
        const kg = value / 1000;
        return `${kg.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;
    }
    return `${value.toLocaleString('pt-BR')} g`;
}

export function summarizePrinterStatsProduction(stats) {
    return (stats || []).reduce((acc, row) => ({
        total_duration_minutes: acc.total_duration_minutes + (Number(row?.total_duration_minutes) || 0),
        total_filament_grams: acc.total_filament_grams + (Number(row?.total_filament_grams) || 0),
        total_jobs: acc.total_jobs + (Number(row?.total_jobs) || 0),
        total_objects: acc.total_objects + (Number(row?.total_objects) || 0),
        total_printed: acc.total_printed + (Number(row?.total_printed) || 0),
        filament_pending_jobs: acc.filament_pending_jobs + (Number(row?.filament_pending_jobs) || 0),
    }), {
        total_duration_minutes: 0,
        total_filament_grams: 0,
        total_jobs: 0,
        total_objects: 0,
        total_printed: 0,
        filament_pending_jobs: 0,
    });
}

export function successRateFromTotals(totalPrinted, totalObjects) {
    if (!totalObjects) {
        return 0;
    }
    return Math.round((totalPrinted / totalObjects) * 1000) / 10;
}

export function toApiDateTime(inputValue) {
    return localInputToApiDateTime(inputValue);
}

export function jobFromApi(data) {
    return {
        printer_uuid: data.printer_uuid || '',
        file_name: data.file_name || '',
        estimated_duration: minutesToDuration(data.estimated_duration_minutes ?? 0),
        estimated_filament_consumption_grams: data.estimated_filament_consumption_grams ?? 0,
        actual_filament_consumption_grams: data.actual_filament_consumption_grams ?? '',
        started_at: toInputDateTime(data.started_at),
        finished_at: toInputDateTime(data.finished_at),
        status: data.status || 'sucesso',
        status_reason: data.status_reason || '',
        object_count: data.object_count ?? 1,
        printed_count: data.printed_count ?? 0,
    };
}

export function buildJobPayload(form, filamentRows) {
    const payload = {
        printer_uuid: form.printer_uuid,
        file_name: form.file_name,
        estimated_duration_minutes: durationToMinutes(form.estimated_duration) ?? 0,
        estimated_filament_consumption_grams: parseInt(form.estimated_filament_consumption_grams, 10) || 0,
        status: form.status,
        object_count: parseInt(form.object_count, 10) || 1,
        printed_count: parseInt(form.printed_count, 10) || 0,
    };

    // Só faz sentido nos status que desviaram do previsto; nos demais o estimado
    // continua valendo como total.
    if (requiresActualConsumption(form.status)) {
        const actual = parseInt(form.actual_filament_consumption_grams, 10);
        if (Number.isFinite(actual)) {
            payload.actual_filament_consumption_grams = actual;
        }
    }

    const started = toApiDateTime(form.started_at);
    const finished = toApiDateTime(form.finished_at);
    if (started) {
        payload.started_at = started;
    }
    if (finished) {
        payload.finished_at = finished;
    }

    if (STATUSES_REQUIRING_REASON.includes(form.status) && form.status_reason?.trim()) {
        payload.status_reason = form.status_reason.trim();
    }

    // Sempre enviado como array, mesmo vazio: é assim que o backend distingue
    // "sem filamentos" de "não mexi nos filamentos".
    payload.filaments = (filamentRows || [])
        .filter((row) => String(row?.filament_uuid || '').trim())
        .map((row) => ({
            filament_uuid: String(row.filament_uuid).trim(),
            grams: parseInt(row.grams, 10) || 0,
        }));

    return payload;
}
